import {QuestionCircleTwoTone} from "@ant-design/icons";
import {AntDValueEditor, AntDValueSelector, QueryBuilderAntD} from "@react-querybuilder/antd";
import {QueryBuilderDnD} from "@react-querybuilder/dnd";
import {FormInstance, InputNumber, Select, Tooltip} from "antd";
import React, {useContext, useEffect, useState} from "react";
import * as ReactDnD from "react-dnd";
import * as ReactDndHtml5Backend from "react-dnd-html5-backend";
import MarkdownContent from "../common/MarkdownContent";
import {Field, OptionGroup, QueryBuilder, RuleGroupType, RuleType, transformQuery, ValueEditorProps} from "react-querybuilder";
import {AttributeDefinitionServiceContext, VulSourceServiceContext} from "../../Contexts";
import AttributeDefinition from "../../domain/AttributeDefinition";
import VulSource, {vulSourceSystemLastComparator} from "../../domain/VulSource";
import {formatVulSourceType, VulSourceType} from "../../domain/VulSourceType";
import QueryOptions from "../../sal-ui/QueryOptions";
import {matchesSearchText} from "../../utils/StringUtils";
import * as tackStyles from "../vulsourceitem/Tack.module.css";
import * as descriptionStyles from "../SelectItemDescription.module.css";

const numericOperators = [
    {name: "ge", label: "≥"},
    {name: "gt", label: ">"},
    {name: "eq", label: "="},
    {name: "ne", label: "≠"},
    {name: "lt", label: "<"},
    {name: "le", label: "≤"}
];

const equalityOperators = [
    {name: "eq", label: "="},
    {name: "ne", label: "≠"}
];

const attributeOperators = [
    {name: "eq", label: "changed"}
];

function createDefaultQuery(): RuleGroupType {
    return {
        combinator: "or",
        rules: []
    };
}

interface Props {
    form?: FormInstance;
    initialQuery?: string;
}

type SearchableFieldSelectorProps = React.ComponentProps<typeof AntDValueSelector>;

function SearchableFieldSelector(selectorProps: SearchableFieldSelectorProps) {
    return (
        <AntDValueSelector
            {...selectorProps}
            showSearch={true}
            filterOption={(input, option) => matchesSearchText(input, option?.label)}
        />
    );
}

function ItemUpdateProfileQueryBuilder(props: Props) {
    const attributeDefinitionService = useContext(AttributeDefinitionServiceContext);
    const vulSourceService = useContext(VulSourceServiceContext);
    const [attributeDefinitions, setAttributeDefinitions] = useState<AttributeDefinition[]>([]);
    const [vulSources, setVulSources] = useState<VulSource[]>([]);
    const [query, setQuery] = useState<RuleGroupType>(() => createDefaultQuery());

    useEffect(() => {
        if (props.initialQuery !== undefined) {
            const initialQuery = JSON.parse(props.initialQuery);
            const transformedQuery = transformQuery(initialQuery, {ruleProcessor: toEditorRule});

            setQuery(transformedQuery);
        } else {
            const defaultQuery = createDefaultQuery();

            setQuery(defaultQuery);
            props.form?.setFieldValue("query", JSON.stringify(defaultQuery));
        }
    }, [props.initialQuery]);

    useEffect(() => {
        attributeDefinitionService.getList(QueryOptions.newUnlimitedOrderedInstance("name")).then(value => setAttributeDefinitions(value.data));
        vulSourceService.getList(QueryOptions.newUnlimitedOrderedInstance("name")).then(value => setVulSources(value.data));
    }, []);

    const fields: OptionGroup<Field>[] = [
        {
            label: "source",
            options: [
                {
                    name: "source.id",
                    label: "source",
                    operators: equalityOperators,
                    valueEditorType: "select",
                    values: vulSources
                },
                {
                    name: "source.type",
                    label: "source type",
                    operators: equalityOperators,
                    valueEditorType: "select",
                    values: Object.values(VulSourceType).map(value => ({
                        name: value,
                        label: formatVulSourceType(value)
                    }))
                }
            ]
        },
        {
            label: "item",
            options: [{
                name: "itemPublishedAgeDays",
                label: "item published age (days)",
                operators: numericOperators,
                inputType: "number"
            }]
        },
        {
            label: "attribute change",
            options: [
                {
                    name: "changeType",
                    label: "change type",
                    operators: equalityOperators,
                    valueEditorType: "select",
                    values: [
                        {name: "ADDED", label: "Added"},
                        {name: "REMOVED", label: "Removed"},
                        {name: "UPDATED", label: "Updated"}
                    ]
                },
                {
                    name: "numericDifference",
                    label: "numeric difference",
                    operators: numericOperators,
                    inputType: "number"
                }
            ]
        },
        {
            label: "attributes",
            options: attributeDefinitions.map(definition => ({
                name: `attribute-${definition.name}`,
                label: definition.name!,
                operators: attributeOperators
            }))
        }
    ];

    return (
        <QueryBuilderDnD dnd={{...ReactDnD, ...ReactDndHtml5Backend}}>
            <QueryBuilderAntD>
                <QueryBuilder
                    fields={fields}
                    query={query}
                    controlElements={{
                        fieldSelector: SearchableFieldSelector,
                        valueEditor: ItemUpdateProfileValueEditor
                    }}
                    onQueryChange={onQueryChange}
                    listsAsArrays={true}
                    getDefaultValue={() => undefined}
                />
            </QueryBuilderAntD>
        </QueryBuilderDnD>
    );

    function onQueryChange(q: RuleGroupType) {
        setQuery(q);

        const transformedQuery = transformQuery(q, {ruleProcessor: toPersistedRule});

        props.form?.setFieldValue("query", JSON.stringify(transformedQuery));
    }

    function toEditorRule(rule: RuleType): RuleType {
        if (rule.field === "attribute") {
            return {
                ...rule,
                field: `attribute-${rule.value}`,
                value: undefined
            };
        }

        return rule;
    }

    function toPersistedRule(rule: RuleType): RuleType {
        if (rule.field.startsWith("attribute-")) {
            return {
                ...rule,
                field: "attribute",
                value: rule.field.substring("attribute-".length)
            };
        }

        return rule;
    }
}

function ItemUpdateProfileValueEditor(props: ValueEditorProps) {
    if (props.fieldData.name.startsWith("attribute-")) {
        return null;
    }

    if (props.fieldData.name === "source.id") {
        const sources = [...(props.fieldData.values ?? [])].sort(vulSourceSystemLastComparator);

        return (
            <Select
                showSearch={true}
                filterOption={(inputValue, option) => matchesSearchText(inputValue, option?.label)}
                popupMatchSelectWidth={350}
                optionLabelProp="label"
                virtual={false}
                value={props.value}
                onChange={value => props.handleOnChange(value)}
            >
                {sources?.map(source =>
                    <Select.Option key={source.id} value={source.id} label={source.name}>
                        {source.owner === "SYSTEM" && <div className={`${tackStyles.tack} ${tackStyles["tack-seal"]}`}><span className={tackStyles["tack-text"]} color="#440084">SEAL</span></div>}
                        {source.owner === "ORGANIZATION" && <div className={`${tackStyles.tack} ${tackStyles["tack-org"]}`}><span className={tackStyles["tack-text"]} color="#87d068">ORG</span></div>}

                        <div className={tackStyles.item}>
                            <div className={descriptionStyles["name-container"]}>
                                <span className={descriptionStyles.name}>{source.name}</span>
                                {source.description && <Tooltip title={<MarkdownContent children={source.description}/>}><QuestionCircleTwoTone/></Tooltip>}
                            </div>
                            <div className={descriptionStyles.description}>{source.description}</div>
                        </div>
                    </Select.Option>
                )}
            </Select>
        );
    }

    if (props.fieldData.inputType === "number") {
        return (
            <InputNumber
                value={props.value}
                onChange={value => props.handleOnChange(value)}
            />
        );
    }

    if (props.fieldData.valueEditorType === "select") {
        return (
            <Select
                showSearch={true}
                style={{minWidth: 130}}
                popupMatchSelectWidth={false}
                value={props.value}
                optionFilterProp="label"
                options={props.fieldData.values?.map(value => ({
                    value: value.name,
                    label: value.label
                }))}
                onChange={value => props.handleOnChange(value)}
            />
        );
    }

    return <AntDValueEditor {...props}/>;
}

export default ItemUpdateProfileQueryBuilder;
